import React from 'react';
import { Divider, TextField, Typography } from '@mui/material';
import type {
  CodeAgentPromptBudgetSettings,
  CodeAgentWebToolsSettings,
} from '../../types/code-agent';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';
import { codeAgentFieldSx } from './codeAgentFieldStyles';

export const DEFAULT_PROMPT_BUDGET: CodeAgentPromptBudgetSettings = {
  promptRatio: 0.7,
  compactAtRatio: 0.8,
  compactAtMaxTokens: 60000,
  recentTranscriptRatio: 0.6,
  dynamicBlockMaxTokens: 3000,
  historicalToolMaxChars: 1000,
  retrievedContextMaxTokens: 1200,
};

export const DEFAULT_WEB_TOOLS: CodeAgentWebToolsSettings = {
  blockedDomains: [],
  maxSearchesPerTurn: 3,
};

type Props = {
  promptBudget: CodeAgentPromptBudgetSettings;
  webTools: CodeAgentWebToolsSettings;
  onPromptBudgetChange: (value: CodeAgentPromptBudgetSettings) => void;
  onWebToolsChange: (value: CodeAgentWebToolsSettings) => void;
};

const ratioFields: Array<{ key: keyof CodeAgentPromptBudgetSettings; label: string }> = [
  { key: 'promptRatio', label: 'Prompt cap (× window)' },
  { key: 'compactAtRatio', label: 'Compact at (× cap)' },
  { key: 'recentTranscriptRatio', label: 'Recent transcript (× cap)' },
];

const tokenFields: Array<{ key: keyof CodeAgentPromptBudgetSettings; label: string }> = [
  { key: 'compactAtMaxTokens', label: 'Compact ceiling (tokens)' },
  { key: 'dynamicBlockMaxTokens', label: 'Dynamic block (tokens)' },
  { key: 'historicalToolMaxChars', label: 'Old tool result (chars)' },
  { key: 'retrievedContextMaxTokens', label: 'Retrieval (tokens)' },
];

/** Spec 02 §4.3: prompt budget ratios (K10 policy tab). */
export function CodeAgentPromptBudgetSection({
  promptBudget,
  webTools,
  onPromptBudgetChange,
  onWebToolsChange,
}: Props) {
  const setField = (key: keyof CodeAgentPromptBudgetSettings, raw: string) => {
    const parsed = parseFloat(raw);
    onPromptBudgetChange({
      ...promptBudget,
      [key]: Number.isFinite(parsed) ? parsed : DEFAULT_PROMPT_BUDGET[key],
    });
  };

  const setIntegerField = (key: keyof CodeAgentPromptBudgetSettings, value: number | undefined) => {
    onPromptBudgetChange({
      ...promptBudget,
      [key]: value || DEFAULT_PROMPT_BUDGET[key],
    });
  };

  return (
    <>
      <Divider className="dark:border-slate-700" />
      <div>
        <Typography className="mb-1 font-semibold text-gray-900 dark:text-slate-100">
          Prompt budget (Spec 02, used when CODE_AGENT_PROMPT_CACHE is on)
        </Typography>
        <Typography variant="body2" className="mb-3 text-gray-500 dark:text-slate-400">
          The prompt cap is the smallest candidate model window × ratio (clamped 16k–200k tokens).
          History is compacted with the fixed summary template once it exceeds the compact
          threshold; the dynamic block holds working memory, ledger and retrieval at the end of the
          prompt.
        </Typography>
        <div className="mb-3 grid grid-cols-3 gap-3">
          {ratioFields.map((field) => (
            <TextField
              key={field.key}
              size="small"
              label={field.label}
              type="number"
              value={promptBudget[field.key]}
              onChange={(e) => setField(field.key, e.target.value)}
              sx={codeAgentFieldSx}
              inputProps={{ min: 0.05, max: 1, step: 0.05 }}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {tokenFields.map((field) => (
            <FormattedNumberTextField
              key={field.key}
              size="small"
              label={field.label}
              value={promptBudget[field.key]}
              onValueChange={(value) => setIntegerField(field.key, value)}
              sx={codeAgentFieldSx}
              inputProps={{ min: 0 }}
            />
          ))}
        </div>
      </div>

      <Divider className="dark:border-slate-700" />
      <div>
        <Typography className="mb-1 font-semibold text-gray-900 dark:text-slate-100">
          Web tools (Spec 03, used when CODE_AGENT_WEB_TOOLS is on)
        </Typography>
        <Typography variant="body2" className="mb-3 text-gray-500 dark:text-slate-400">
          web_search / web_fetch run on the server with network risk (approval in review mode).
          Blocked domains are refused by web_fetch; searches above the per-turn limit are skipped.
        </Typography>
        <div className="grid grid-cols-2 gap-3">
          <FormattedNumberTextField
            size="small"
            label="Max web_search per turn"
            value={webTools.maxSearchesPerTurn}
            onValueChange={(value) =>
              onWebToolsChange({
                ...webTools,
                maxSearchesPerTurn: value || DEFAULT_WEB_TOOLS.maxSearchesPerTurn,
              })
            }
            sx={codeAgentFieldSx}
            inputProps={{ min: 1 }}
          />
          <TextField
            size="small"
            label="Blocked domains (one per line)"
            multiline
            minRows={2}
            value={webTools.blockedDomains.join('\n')}
            onChange={(e) =>
              onWebToolsChange({
                ...webTools,
                blockedDomains: e.target.value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean),
              })
            }
            sx={codeAgentFieldSx}
          />
        </div>
      </div>
    </>
  );
}
